import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { playerAPI } from '../lib/api';
import { Pause, Play, SignOut, TennisBall, PencilSimple, Camera, ChatCircleText, EnvelopeSimple, BellSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Avatar from '../components/Avatar';

export default function SettingsPage() {
  const { user, player, logout, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editing] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Display name: prefer first + last (the editable source of truth), fall back to the
  // legacy `name` field, then email. This stays in sync after edits because refreshUser()
  // re-fetches /auth/me which returns the updated profile row.
  const displayName =
    `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
    user?.name ||
    user?.email ||
    '';

  const handlePauseResume = async () => {
    setLoading(true);
    try {
      if (player?.status === 'active') {
        await playerAPI.pause();
        toast.success('You are now paused. Return within 7 days to keep your rank.');
      } else {
        const res = await playerAPI.resume();
        toast.success(res.data.message || 'Welcome back!');
      }
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be under 3 MB');
      return;
    }
    setAvatarLoading(true);
    try {
      await playerAPI.uploadAvatar(file);
      toast.success('Profile photo updated');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setAvatarLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">Settings</h1>
        <p className="text-sm text-[#6a766e] mt-1">Manage your account and profile</p>
      </div>

      {/* Profile card */}
      <div className="bg-[#f0ece3] rounded-2xl border border-[#e3dcd1] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar
                src={user?.avatar_url}
                name={displayName}
                size="md"
                testId="settings-avatar"
              />
              <button
                type="button"
                data-testid="upload-avatar-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarLoading}
                title="Change photo"
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#015e3f] text-white flex items-center justify-center shadow-md border-2 border-[#f0ece3] active:scale-95 transition-transform disabled:opacity-60"
              >
                {avatarLoading ? (
                  <span className="block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera size={12} weight="fill" />
                )}
              </button>
              <input
                ref={fileInputRef}
                data-testid="avatar-file-input"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
            <div>
              <div className="font-medium text-[#013523]" data-testid="settings-display-name">{displayName}</div>
              <div className="text-sm text-[#6a766e]" data-testid="settings-email">{user?.email}</div>
              {player && (
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    player.status === 'active' ? 'bg-[#015e3f]/10 text-[#015e3f]' : 'bg-[#6a766e]/10 text-[#6a766e]'
                  }`}>
                    {player.status === 'active' ? `Rank #${player.rank}` : 'Paused'}
                  </span>
                  {player.usta_rating && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#c2a251]/10 text-[#c2a251]">
                      USTA {player.usta_rating}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!editing && (
              <button
                onClick={() => navigate(`/player/${user?.id}?edit=1`)}
                className="text-[#6a766e] hover:text-[#015e3f] p-1"
                data-testid="edit-profile-button"
                title="Edit profile"
              >
                <PencilSimple size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Read-only profile info — available for every member, ladder or not */}
        <div className="mt-3 pt-3 border-t border-[#e3dcd1] grid grid-cols-2 gap-x-4 gap-y-1">
          {(player?.phone || user?.phone) && <ProfileField label="Phone" value={player?.phone || user?.phone} />}
          {(player?.usta_rating || user?.usta_rating) && <ProfileField label="USTA" value={player?.usta_rating || user?.usta_rating} />}
          <ProfileField label="Email alerts" value={user?.email_opt_in ? 'On' : 'Off'} />
          <ProfileField label="SMS alerts" value={user?.sms_opt_in ? 'On' : 'Off'} />
        </div>
      </div>

      {/* Gender — Gender Slice MVP. Required for Men's / Ladies Night
          eligibility. Inline editable; saves via existing /players/profile
          PATCH which now accepts an optional `gender` field. */}
      <GenderSection user={user} refreshUser={refreshUser} />

      {/* Notification Preferences (A2P 10DLC compliance — user control over SMS at any time) */}
      <NotificationPreferences user={user} refreshUser={refreshUser} />

      {/* Pause/Resume */}
      {player && (
        <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#f0ece3] flex items-center justify-center shrink-0">
              {player.status === 'active' ? <Pause size={20} className="text-[#6a766e]" /> : <Play size={20} className="text-[#015e3f]" />}
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#013523]">
                {player.status === 'active' ? 'Pause Participation' : 'Resume Playing'}
              </div>
              <p className="text-xs text-[#6a766e] mt-1">
                {player.status === 'active'
                  ? 'Taking a break? Your rank is held for 7 days. After that, a small penalty applies based on how long you\'re away.'
                  : 'Ready to play again? Your return rank depends on how long you\'ve been paused (0-7 days = same rank, 8-14 = -1, 15-21 = -2, 22-30 = -3, 31+ = last).'}
              </p>
              <button
                data-testid="pause-resume-button"
                onClick={handlePauseResume}
                disabled={loading}
                className={`mt-3 rounded-full px-5 py-2 text-sm font-medium transition-transform active:scale-95 ${
                  player.status === 'active'
                    ? 'bg-[#f0ece3] text-[#013523] border border-[#e3dcd1]'
                    : 'bg-[#015e3f] text-white'
                } disabled:opacity-50`}
              >
                {loading ? 'Updating...' : player.status === 'active' ? 'Pause' : 'Resume'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout */}
      <button
        data-testid="logout-button"
        onClick={logout}
        className="w-full bg-red-50 text-red-600 border border-red-200 rounded-full px-6 py-3 font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
      >
        <SignOut size={20} />
        Sign Out
      </button>

      <div className="text-center pt-4">
        <div className="inline-flex items-center gap-1.5 text-[#e3dcd1]">
          <TennisBall size={16} weight="duotone" />
          <span className="text-xs tracking-wider uppercase">The Rippers Club v1.0</span>
        </div>
        <div
          data-testid="settings-legal-links"
          className="mt-3 flex flex-wrap items-center justify-center gap-3 text-[11px] text-[#6a766e]"
        >
          <Link to="/privacy" className="hover:text-[#015e3f]" data-testid="settings-privacy-link">
            Privacy Policy
          </Link>
          <span aria-hidden="true" className="text-[#e3dcd1]">•</span>
          <Link to="/terms" className="hover:text-[#015e3f]" data-testid="settings-terms-link">
            Terms & Conditions
          </Link>
          <span aria-hidden="true" className="text-[#e3dcd1]">•</span>
          <Link to="/sms-terms" className="hover:text-[#015e3f]" data-testid="settings-sms-terms-link">
            SMS Terms
          </Link>
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wider text-[#6a766e]">{label}</span>
      <div className="text-sm text-[#2a332c]">{value}</div>
    </div>
  );
}

/**
 * Notification Preferences card. Lets the user enable/disable SMS and Email
 * notifications at any time — required for A2P 10DLC compliance (the user must
 * always be able to opt out of SMS without losing account access).
 *
 * Push notifications are surfaced as a coming-soon toggle so the UI shows the
 * full notification surface area for compliance review.
 */
function NotificationPreferences({ user, refreshUser }) {
  const hasPhone = !!(user?.phone && String(user.phone).trim());
  const [smsOn, setSmsOn] = useState(!!user?.sms_opt_in);
  const [emailOn, setEmailOn] = useState(!!user?.email_opt_in);
  const [savingKey, setSavingKey] = useState(null);

  const persist = async (key, value) => {
    setSavingKey(key);
    try {
      await playerAPI.updateNotificationPrefs({ [key]: value });
      toast.success('Preferences saved');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save preferences');
      // Roll back UI state on failure
      if (key === 'sms_opt_in') setSmsOn(!value);
      if (key === 'email_opt_in') setEmailOn(!value);
    } finally {
      setSavingKey(null);
    }
  };

  const onToggleSms = (val) => {
    if (!hasPhone && val) {
      toast.error('Add a phone number to enable SMS notifications.');
      return;
    }
    setSmsOn(val);
    persist('sms_opt_in', val);
  };
  const onToggleEmail = (val) => { setEmailOn(val); persist('email_opt_in', val); };

  // --- Verified SMS consent (distinct from the preference toggle) ---
  const [smsStatus, setSmsStatus] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const isImpersonation = (localStorage.getItem('tl_session_token') || '').startsWith('imp_');

  const loadStatus = useCallback(async () => {
    try { const r = await playerAPI.getSmsStatus(); setSmsStatus(r.data); } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const confirmConsent = async () => {
    setConfirming(true);
    try {
      await playerAPI.confirmSmsConsent();
      toast.success("SMS consent confirmed — you're verified.");
      setAgreed(false);
      await refreshUser();
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm SMS consent');
    } finally {
      setConfirming(false);
    }
  };

  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

  return (
    <div
      data-testid="notification-preferences-card"
      className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-5"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#f0ece3] flex items-center justify-center shrink-0">
          <BellSimple size={20} className="text-[#015e3f]" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-[#013523]">Notification Preferences</div>
          <p className="text-xs text-[#6a766e] mt-1">
            Choose how you want to hear about ladder activity. SMS is optional — you can turn it off at any time.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <PrefRow
          testId="pref-sms"
          icon={<ChatCircleText size={18} className="text-[#015e3f]" />}
          title="SMS Notifications"
          subtitle={
            hasPhone
              ? 'Challenge alerts, match reminders, and account messages. Reply STOP at any time to opt out.'
              : 'Add a phone number to enable SMS notifications.'
          }
          checked={smsOn && hasPhone}
          onChange={onToggleSms}
          busy={savingKey === 'sms_opt_in'}
          disabled={!hasPhone}
        />
        <PrefRow
          testId="pref-email"
          icon={<EnvelopeSimple size={18} className="text-[#015e3f]" />}
          title="Email Notifications"
          subtitle="Account updates and important ladder announcements."
          checked={emailOn}
          onChange={onToggleEmail}
          busy={savingKey === 'email_opt_in'}
        />
        <PrefRow
          testId="pref-push"
          icon={<BellSimple size={18} className="text-[#6a766e]" />}
          title="Push Notifications"
          subtitle="Coming soon — get instant alerts on your device."
          checked={false}
          onChange={() => {}}
          disabled
        />
      </div>

      {/* Verified SMS consent — the compliance basis for sending (separate
          from the preference toggle above). */}
      {hasPhone && smsStatus && (
        <div data-testid="sms-consent-section" className="mt-3">
          {smsStatus.suppressed ? (
            <div data-testid="sms-consent-suppressed" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">
              You previously texted <strong>STOP</strong> from this number. Reply <strong>START</strong> from your phone to resume club texts — for your protection this can't be re-enabled here.
            </div>
          ) : smsStatus.sms_consent ? (
            <div data-testid="sms-consent-verified" className="rounded-xl border border-[#015e3f]/30 bg-[#015e3f]/8 px-4 py-3 text-[12px] text-[#013523]">
              <span className="font-semibold">✓ SMS consent verified</span>
              {smsStatus.sms_consent_timestamp && <> on {fmtDate(smsStatus.sms_consent_timestamp)}</>}. You're eligible to receive club text messages.
            </div>
          ) : (
            <div data-testid="sms-consent-needed" className="rounded-xl border border-[#c2a251]/50 bg-[#c2a251]/12 px-4 py-3">
              <div className="text-[13px] font-semibold text-[#8a6d2c]">
                {smsStatus.sms_opt_in ? 'SMS permission incomplete' : 'Confirm SMS consent to receive texts'}
              </div>
              <p className="text-[11px] text-[#6a766e] mt-1 leading-relaxed">
                {smsStatus.sms_opt_in
                  ? 'Your SMS notifications are on, but we don\'t yet have a verified consent record — so the club can\'t text you. Please confirm below.'
                  : 'Give explicit consent to receive club text messages.'}
              </p>
              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="sms-consent-checkbox"
                  checked={agreed}
                  disabled={isImpersonation}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#015e3f]"
                />
                <span className="text-[11px] text-[#2a332c] leading-relaxed">
                  I agree to receive recurring automated text messages from The Rippers Club (challenge alerts,
                  match reminders, and account messages) at the mobile number on my account. Msg &amp; data rates
                  may apply; message frequency varies. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help.
                  Consent is not a condition of membership. See our{' '}
                  <Link to="/sms-terms" className="underline hover:text-[#015e3f]" data-testid="sms-consent-terms-link">SMS Terms &amp; Privacy</Link>.
                </span>
              </label>
              {isImpersonation ? (
                <p data-testid="sms-consent-impersonation-note" className="text-[11px] text-red-600 mt-2">
                  Consent must be given by the member themselves and cannot be recorded during an admin impersonation session.
                </p>
              ) : (
                <button
                  type="button"
                  data-testid="sms-consent-confirm-btn"
                  disabled={!agreed || confirming}
                  onClick={confirmConsent}
                  className="mt-3 inline-flex items-center rounded-full bg-[#015e3f] hover:bg-[#014d34] text-white text-xs font-medium px-4 py-2 min-h-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirming ? 'Saving…' : 'Confirm SMS consent'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-[#6a766e] mt-3">
        SMS consent is optional and is not required to create or use a Ladder account.
        Read the full <Link to="/sms-terms" className="underline hover:text-[#015e3f]" data-testid="pref-sms-terms-link">SMS Terms</Link>.
      </p>
    </div>
  );
}

function PrefRow({ icon, title, subtitle, checked, onChange, busy, disabled, testId }) {
  return (
    <div
      data-testid={testId}
      className={`flex items-start justify-between gap-3 rounded-xl border border-[#e3dcd1] bg-white px-4 py-3 ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-[#f0ece3] flex items-center justify-center shrink-0 mt-0.5">
          {icon}
        </div>
        <div>
          <div className="text-sm font-medium text-[#013523]">{title}</div>
          <p className="text-[11px] text-[#6a766e] leading-relaxed">{subtitle}</p>
        </div>
      </div>
      <Toggle
        testId={`${testId}-toggle`}
        checked={checked}
        onChange={onChange}
        busy={busy}
        disabled={disabled}
      />
    </div>
  );
}

function Toggle({ checked, onChange, busy, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
        checked ? 'bg-[#015e3f]' : 'bg-[#d9d2c5]'
      } ${disabled ? 'cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 mt-0.5 ${
          checked ? 'translate-x-[22px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  );
}

function GenderSection({ user, refreshUser }) {
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState(user?.gender || '');

  const labelOf = (g) => ({
    male: 'Male',
    female: 'Female',
  }[g] || 'Not set');

  const save = async (g) => {
    setBusy(true);
    try {
      await playerAPI.updateProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || null,
        usta_rating: user.usta_rating || null,
        gender: g,
        email_opt_in: !!user.email_opt_in,
        sms_opt_in: !!user.sms_opt_in,
      });
      setVal(g);
      await refreshUser?.();
      toast.success('Gender updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-5" data-testid="settings-gender-card">
      <div className="font-medium text-[#013523] mb-1">Gender</div>
      <p className="text-xs text-[#6a766e] mb-3">
        Required to register for Men's Night or Ladies Night events.
        {!val && <span className="block mt-1 text-amber-700">Please set your gender to register for gender-restricted events.</span>}
      </p>
      <div className="flex flex-wrap gap-2">
        {['male', 'female'].map((g) => (
          <button
            key={g}
            type="button"
            disabled={busy}
            onClick={() => save(g)}
            data-testid={`settings-gender-${g}`}
            className={`text-xs px-3 py-1.5 rounded-lg border transition ${
              val === g
                ? 'bg-[#015e3f] text-white border-[#015e3f]'
                : 'bg-white text-[#013523] border-[#e3dcd1]'
            } disabled:opacity-50`}
          >
            {labelOf(g)}
          </button>
        ))}
      </div>
    </div>
  );
}

